import { useEffect, useRef, useState } from 'react';
import { audio } from '@tt/audio';
import tavernBgUrl from '../../assets/tavern-exterior-bg.svg?url';
import tavernBgLandscapeUrl from '../../assets/tavern-exterior-bg-landscape.svg?url';

interface Props { onSuccess: () => void }

/**
 * Cinematic open-gate intro. The passcode is gone — the door is always
 * unlocked. Single "Enter the Tavern" CTA POSTs to /api/auth/gate to mint
 * the `tt_gate` session cookie, then transitions into the app.
 *
 * Layout (back → front):
 *   1. Sky-fill underlay (.gate-bg-fill) — gradient night sky filling
 *      letterbox bars on landscape viewports.
 *   2. .gate-bg <img> — full hand-crafted tavern exterior SVG (885 lines,
 *      portrait 800×1200 viewBox). object-fit: contain so the SVG
 *      ALWAYS fits within viewport on every aspect ratio. Mobile portrait
 *      fills the screen; landscape gets sky-fill on either side.
 *   3. .gate-door-svg — separate door panel inline SVG, scaled to match
 *      the bg via xMidYMid meet (matches the bg's contained box exactly).
 *      Pivots on its left hinge for the open animation.
 *   4. Ember canvas — drifting warm particles.
 *   5. CTA + tagline + footer (always on top).
 */
export function Gate({ onSuccess }: Props) {
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutsRef = useRef<number[]>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Cancel pending success-callback timers if the component unmounts mid-anim.
  useEffect(() => () => {
    for (const id of timeoutsRef.current) window.clearTimeout(id);
    timeoutsRef.current = [];
  }, []);

  // Drifting ember canvas (same particle system used in Background).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduced) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let w = canvas.width = window.innerWidth;
    let h = canvas.height = window.innerHeight;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    interface Ember { x: number; y: number; vx: number; vy: number; r: number; life: number; max: number; hue: number }
    const embers: Ember[] = [];
    const target = Math.min(60, Math.max(24, Math.floor((w * h) / 24000)));
    function spawn(): Ember {
      return {
        x: Math.random() * w,
        y: h + 10,
        vx: (Math.random() - 0.5) * 0.3,
        vy: -(0.3 + Math.random() * 0.6),
        r: 0.6 + Math.random() * 1.4,
        life: 0,
        max: 3000 + Math.random() * 3000,
        hue: 18 + Math.random() * 40,
      };
    }
    while (embers.length < target) embers.push(spawn());
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(50, now - last); last = now;
      ctx.clearRect(0, 0, w, h);
      for (const e of embers) {
        e.life += dt;
        e.x += e.vx * (dt / 16);
        e.y += e.vy * (dt / 16);
        if (e.life > e.max || e.y < -10) Object.assign(e, spawn());
        const lifeT = e.life / e.max;
        const alpha = lifeT < 0.2 ? lifeT / 0.2 : 1 - (lifeT - 0.2) / 0.8;
        ctx.fillStyle = `hsla(${e.hue}, 90%, 60%, ${alpha * 0.7})`;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const onResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, []);

  async function open() {
    if (busy || opening) return;
    setError(null);
    try {
      audio.unlock();
      audio.setMood('gate');
      const sfx = audio.sfx as Partial<{ doorOpen: () => void; bell: () => void }>;
      if (typeof sfx.doorOpen === 'function') sfx.doorOpen();
      else if (typeof sfx.bell === 'function') sfx.bell();
    } catch { /* audio is best-effort */ }
    setBusy(true);
    setOpening(true);
    try {
      const res = await fetch('/api/auth/gate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enter: true }),
      });
      if (!res.ok) {
        setError(`Couldn't open the door (${res.status}). Tap to try again.`);
        setBusy(false);
        setOpening(false);
        return;
      }
    } catch {
      setError('Network error. Tap to try again.');
      setBusy(false);
      setOpening(false);
      return;
    }
    // Schedule the transition: amber flash peaks at ~600ms, onSuccess at ~1000ms.
    timeoutsRef.current.push(window.setTimeout(() => { onSuccess(); }, 1000));
  }

  return (
    <main
      className={`full-center gate-screen${opening ? ' gate-door-opening' : ''}`}
      onClick={open}
    >
      {/* z=1: sky-fill underlay covering letterbox bars on wide viewports */}
      <div className="gate-bg-fill" aria-hidden="true" />

      {/* z=2: full tavern exterior SVG. Portrait variant fills mobile/portrait
          viewports via object-fit: cover. Landscape variant takes over on
          wide screens via the orientation media query (see global.css). */}
      <img
        className="gate-bg gate-bg-portrait"
        src={tavernBgUrl}
        alt=""
        aria-hidden="true"
        draggable={false}
      />
      <img
        className="gate-bg gate-bg-landscape"
        src={tavernBgLandscapeUrl}
        alt=""
        aria-hidden="true"
        draggable={false}
      />

      {/* z=3: drifting embers */}
      <canvas ref={canvasRef} className="gate-embers" aria-hidden="true" />

      {/* z=5: door panel — portrait variant.
          ViewBox matches tavern-exterior-bg.svg (800×1200). Door hole at
          x=344..456, y=690..938. Pivot on left hinge (x=344). */}
      <div className="gate-door-pivot gate-door-pivot-portrait" aria-hidden="true">
        <svg
          className="gate-door-svg"
          viewBox="0 0 800 1200"
          preserveAspectRatio="xMidYMid slice"
        >
          <g className="gate-door-panel">
            <rect x="344" y="690" width="112" height="248" fill="#2a1808" stroke="#0a0604" strokeWidth="2" />
            <line x1="382" y1="694" x2="382" y2="934" stroke="#0a0604" strokeWidth="1.2" opacity="0.7" />
            <line x1="418" y1="694" x2="418" y2="934" stroke="#0a0604" strokeWidth="1.2" opacity="0.7" />
            <rect x="344" y="710" width="44" height="10" fill="#1a1410" stroke="#3a2820" strokeWidth="0.5" />
            <rect x="344" y="908" width="44" height="10" fill="#1a1410" stroke="#3a2820" strokeWidth="0.5" />
            <circle cx="350" cy="715" r="2.5" fill="#5a4a3a" />
            <circle cx="350" cy="913" r="2.5" fill="#5a4a3a" />
            {/* Brass door knob — backplate + bulb + highlight. */}
            <circle cx="440" cy="820" r="9"   fill="#3a2410" opacity="0.85" />
            <circle cx="440" cy="820" r="7"   fill="#a87a3a" stroke="#3a2410" strokeWidth="1" />
            <circle cx="440" cy="820" r="4.5" fill="#d8a85a" />
            <circle cx="438" cy="818" r="1.6" fill="#fff2c0" opacity="0.95" />
            <circle cx="400" cy="760" r="11" fill="none" stroke="#3a2410" strokeWidth="1.5" />
            <circle cx="400" cy="760" r="9" fill="none" stroke="#4ade7a" strokeWidth="1.2" opacity="0.55" />
            <circle cx="400" cy="760" r="2" fill="#ff4a2a" />
            <rect x="354" y="700" width="92" height="228" fill="none" stroke="#0a0604" strokeWidth="0.6" opacity="0.35" />
          </g>
        </svg>
      </div>

      {/* z=5: door panel — landscape variant.
          ViewBox matches tavern-exterior-bg-landscape.svg (1600×900). Door
          hole at x=754..846, y=656..820. Pivot on left hinge (x=754). */}
      <div className="gate-door-pivot gate-door-pivot-landscape" aria-hidden="true">
        <svg
          className="gate-door-svg"
          viewBox="0 0 1600 900"
          preserveAspectRatio="xMidYMid slice"
        >
          <g className="gate-door-panel">
            <rect x="756" y="656" width="88" height="164" fill="#2a1808" stroke="#0a0604" strokeWidth="2" />
            <line x1="786" y1="660" x2="786" y2="816" stroke="#0a0604" strokeWidth="1.2" opacity="0.7" />
            <line x1="814" y1="660" x2="814" y2="816" stroke="#0a0604" strokeWidth="1.2" opacity="0.7" />
            <rect x="756" y="670" width="34" height="8" fill="#1a1410" stroke="#3a2820" strokeWidth="0.5" />
            <rect x="756" y="800" width="34" height="8" fill="#1a1410" stroke="#3a2820" strokeWidth="0.5" />
            <circle cx="762" cy="674" r="2" fill="#5a4a3a" />
            <circle cx="762" cy="804" r="2" fill="#5a4a3a" />
            {/* Brass door knob — backplate + bulb + highlight. */}
            <circle cx="832" cy="744" r="7"   fill="#3a2410" opacity="0.85" />
            <circle cx="832" cy="744" r="5.5" fill="#a87a3a" stroke="#3a2410" strokeWidth="1" />
            <circle cx="832" cy="744" r="3.4" fill="#d8a85a" />
            <circle cx="830.5" cy="742.5" r="1.2" fill="#fff2c0" opacity="0.95" />
            <circle cx="800" cy="702" r="9" fill="none" stroke="#3a2410" strokeWidth="1.5" />
            <circle cx="800" cy="702" r="7" fill="none" stroke="#4ade7a" strokeWidth="1.2" opacity="0.55" />
            <circle cx="800" cy="702" r="1.6" fill="#ff4a2a" />
            <rect x="762" y="664" width="76" height="148" fill="none" stroke="#0a0604" strokeWidth="0.6" opacity="0.35" />
          </g>
        </svg>
      </div>

      {/* z=10: tagline (above the cabin) */}
      <p className="gate-tagline display" aria-hidden="true">tonight at the table…</p>

      {/* z=10: CTA + footer (matches existing CSS vocabulary) */}
      <div className="gate-cta-row">
        <button
          type="button"
          className="gate-pull-btn gate-enter-btn"
          onClick={(e) => { e.stopPropagation(); void open(); }}
          disabled={busy || opening}
        >
          <span className="gate-pull-rivet gate-pull-rivet-l" aria-hidden="true" />
          <span className="gate-pull-rivet gate-pull-rivet-r" aria-hidden="true" />
          <span className="gate-pull-label">{opening ? 'opening…' : 'Enter the Tavern'}</span>
        </button>
        <footer className="gate-footer-sign muted" aria-hidden="false">
          <span className="gate-sign-plaque">No accounts. No passwords. Just play.</span>
        </footer>
        {error && <div role="alert" className="gate-rejection">{error}</div>}
      </div>

      {/* z=12: amber overlay flash during open transition */}
      <div className={`gate-overlay${opening ? ' is-flashing' : ''}`} aria-hidden="true" />
    </main>
  );
}
